import { supabase } from '@/lib/supabase'
import type { AHProduct } from '@/services/ah-service'

// Persisted corrections for the ingredient -> AH product dictionary. Every call
// swallows errors so the weekmenu -> AH flow keeps working even before the
// 013_add_ah_product_mappings migration has been applied (falls back to live
// search only).

export interface AHMapping {
  ingredientKey: string
  webshopId: number
  productTitle: string
  unitSize: string | null
}

export const normalizeIngredientKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[.,;()]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const getMappings = async (keys: string[]): Promise<Record<string, AHMapping>> => {
  const unique = [...new Set(keys.map(normalizeIngredientKey).filter(Boolean))]
  if (!unique.length) {
    return {}
  }

  try {
    const { data, error } = await supabase
      .from('ah_product_mappings')
      .select('ingredient_key, webshop_id, product_title, unit_size')
      .in('ingredient_key', unique)

    if (error || !data) {
      return {}
    }

    return Object.fromEntries(
      data.map((row) => [
        row.ingredient_key,
        {
          ingredientKey: row.ingredient_key,
          webshopId: row.webshop_id,
          productTitle: row.product_title,
          unitSize: row.unit_size,
        },
      ])
    )
  } catch {
    return {}
  }
}

const saveMapping = async (ingredientKey: string, product: AHProduct): Promise<void> => {
  const key = normalizeIngredientKey(ingredientKey)
  if (!key || !product.webshopId) {
    return
  }

  try {
    const { data: userData } = await supabase.auth.getUser()
    await supabase.from('ah_product_mappings').upsert(
      {
        ingredient_key: key,
        webshop_id: product.webshopId,
        product_title: product.title,
        unit_size: product.unitSize || null,
        updated_by: userData?.user?.id ?? null,
      },
      { onConflict: 'ingredient_key' }
    )
  } catch {
    // Dictionary is a best-effort cache; ignore write failures.
  }
}

export const ahMappingApi = {
  getMappings,
  saveMapping,
}
